import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { IntegerInput } from '@client/components/NumericInput';
import { addMonths, format } from 'date-fns';

export type ExpirationMode = 'no_end_date' | 'months_from_today' | 'specific_date';

export interface ExpirationValue {
  mode: ExpirationMode;
  monthsFromToday: number;
  specificDate: string;
}

interface SeatExpirationCardProps {
  value: ExpirationValue;
  onChange: (value: ExpirationValue) => void;
  defaultOpen?: boolean;
}

export function SeatExpirationCard({
  value,
  onChange,
  defaultOpen = true,
}: SeatExpirationCardProps) {
  // Calculate expiration date based on months from today
  const calculateExpirationDate = (months: number): string => {
    const today = new Date();
    const expirationDate = addMonths(today, months);

    return format(expirationDate, 'M/d/yyyy');
  };

  // Get today's date in YYYY-MM-DD format for min attribute
  const getTodayString = (): string => {
    const today = new Date();
    return format(today, 'yyyy-MM-dd');
  };

  const handleModeChange = (mode: string) => {
    onChange({ ...value, mode: mode as ExpirationMode });
  };

  const handleMonthsChange = (months: number | null) => {
    onChange({ ...value, monthsFromToday: months ?? 0 });
  };

  const validateMonths = (months: number | null): boolean => {
    // Allow null (empty) or values between 0 and 120
    return months === null || (months >= 0 && months <= 120);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, specificDate: e.target.value });
  };

  return (
    <Card className="border-gray-200">
      <CardContent className="px-4 py-2">
        <Accordion
          type="single"
          collapsible
          defaultValue={defaultOpen ? 'expiration-settings' : undefined}
        >
          <AccordionItem
            value="expiration-settings"
            className="border-none"
          >
            <AccordionTrigger className="px-0 pb-2 hover:no-underline">
              <h3 className="text-sm font-medium text-gray-900">Seat End Date (Optional)</h3>
            </AccordionTrigger>
            <div className="mb-4 border-t border-gray-200"></div>
            <p className="mb-4 text-sm text-gray-700">
              Set when this sponsored seat should expire.
            </p>
            <AccordionContent className="px-0 pt-0">
              <RadioGroup
                value={value.mode}
                onValueChange={handleModeChange}
              >
                <div className="space-y-4">
                  {/* No end date option */}
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem
                      value="no_end_date"
                      id="no_end_date"
                    />
                    <Label
                      htmlFor="no_end_date"
                      className="cursor-pointer font-normal text-gray-900"
                    >
                      No end date
                    </Label>
                  </div>

                  {/* Number of months from today option */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem
                        value="months_from_today"
                        id="months_from_today"
                      />
                      <Label
                        htmlFor="months_from_today"
                        className="cursor-pointer font-normal text-gray-900"
                      >
                        Number of months from today
                      </Label>
                    </div>

                    {value.mode === 'months_from_today' && (
                      <div className="ml-7 space-y-3">
                        <IntegerInput
                          label={`Expiration date: ${calculateExpirationDate(value.monthsFromToday)}`}
                          value={value.monthsFromToday}
                          onChange={handleMonthsChange}
                          onPreview={handleMonthsChange}
                          validator={validateMonths}
                          errorMessage="Maximum 120 months (10 years)"
                          className="w-[90%]"
                          classNames={{
                            container: 'mb-0',
                            box: 'border border-gray-300 rounded-md',
                          }}
                          unit="months"
                        />
                        <div className="rounded-md p-1">
                          <p className="w-[90%] text-xs italic text-gray-700">
                            Note: The seat will expire on this date regardless of any subscription
                            pauses.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Select specific end date option */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem
                        value="specific_date"
                        id="specific_date"
                      />
                      <Label
                        htmlFor="specific_date"
                        className="cursor-pointer font-normal text-gray-900"
                      >
                        Select specific end date
                      </Label>
                    </div>

                    {value.mode === 'specific_date' && (
                      <div className="ml-7">
                        <Input
                          type="date"
                          value={value.specificDate}
                          onChange={handleDateChange}
                          min={getTodayString()}
                          className="w-[90%]"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </RadioGroup>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}
